import { useEffect, useLayoutEffect, useCallback, useRef, useState, useMemo, } from "react"
import { READ_DRAWBOOKLOOKUP } from "../../config/api"
import { S3CLOUD_CLOUDFRONT } from "../../config/s3directories"
import { iconwrapper, pathsToImage, pixelateImage } from "../../config/commonfunctions"
import { CROSS_ICON } from "../../config/svgicons"
import { Checkdevice, ScreenHeight } from "../../components"
import { LEFTARROW_ICON_SMALL, RIGHTARROW_ICON_SMALL, guestbook_doodle_strokewt, guestbooklookupstub } from "../../config/constants"
import Guestbookportal from "./components/Guestbookportal"
import pako from 'pako'
import axios from 'axios'
import * as d3 from 'd3';
import './Guestbooklookup.css'

const dimroot = 100
const dim = 60
const PIXELATED = false

const forcestrength = 0.05
const collideconstant = 5

function pointsToPath(points) {
    if (!points.length) return "";
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
        d += ` L ${points[i].x} ${points[i].y}`;
    }
    return d;
}

function getBounds(paths) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    paths.forEach(p => {
        p.points.forEach(pt => {
            minX = Math.min(minX, pt.x);
            minY = Math.min(minY, pt.y);
            maxX = Math.max(maxX, pt.x);
            maxY = Math.max(maxY, pt.y);
        });
    });

    return { minX, minY, maxX, maxY };
}

function panToNode(svg, d, vbwidth, vbheight, lastTransformRef, zoom) {
    const scale = 1;
    const x = (vbwidth / 8) - (d.x * scale);
    const y = (vbheight / 2) - (d.y * scale);
    const t = d3.zoomIdentity.translate(x, y).scale(scale);
    lastTransformRef.current = t;

    svg.transition()
        .duration(1002)
        .ease(d3.easeCubicOut)
        .call(zoom.transform, t);
}

function GuestScatterChartFullscreen({
    guestdata,
    guestdataaddr,
    maxguestnum,
    dim,
    height,
    width,
    nodeclick,
    canvasRefs,
    properties,
    sharedproperties,
    selecteddata,
    bgcolor,
}) {
    const [img, setImg] = useState("");
    const svgRef = useRef();
    const imagesCache = useRef({});
    const lastTransformRef = useRef(d3.zoomIdentity);
    const isFirstRenderRef = useRef(true);


    const vbwidth = 800;
    const vbheight = 600;
    const framestrokewt = 2
    const framebordercolor = '#e4d5b7'
    const dotcolor = 'var(--maindark)'

    useEffect(() => {
        if (sharedproperties && sharedproperties["SCATTER"] && sharedproperties["SCATTER"].allowimg && sharedproperties["SCATTER"].img.length > 0) {
            setImg(S3CLOUD_CLOUDFRONT + sharedproperties["SCATTER"].img);
        }
    }, [sharedproperties, properties]);

    const nodes = useMemo(() => {
        if (!guestdataaddr) return [];
        if (img.length > 0) {
            return [
                { id: "center_image", r: dim, isCenter: true, fx: vbwidth / 2, fy: vbheight / 2 },
                ...Array.from({ length: Number(maxguestnum) }, (_, i) => {
                    const addr = guestdataaddr[i];
                    return addr
                        ? { id: addr, r: dim / 2, guest: guestdata[addr], isCenter: false }
                        : { id: "empty_" + i, r: 0, isCenter: false, empty: true };
                })
            ];
        }
        else {
            return [
                ...Array.from({ length: Number(maxguestnum) }, (_, i) => {
                    const addr = guestdataaddr[i];
                    return addr
                        ? { id: addr, r: dim / 2, guest: guestdata[addr], isCenter: false }
                        : { id: "empty_" + i, r: 0, isCenter: false, empty: true };
                })
            ];
        }

    }, [guestdataaddr, maxguestnum, dim, guestdata, img]);

    useEffect(() => {
        if (!guestdataaddr || !svgRef.current) return;

        guestdataaddr.forEach((id) => {
            const guest = guestdata[id];
            if (guest?.image && !imagesCache.current[id]) {
                imagesCache.current[id] = guest.image;
            }
        });

        const svg = d3.select(svgRef.current);

        svg.selectAll("*").remove();

        const defs = svg.append("defs");

        defs.append("clipPath")
            .attr("id", "clip-center")
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", dim);

        const zoomGroup = svg.append("g");      // Doodles layer
        const overlayGroup = svg.append("g");   // Overlay UI layer (ALWAYS ON TOP)

        const zoom = d3.zoom()
            .scaleExtent([0.5, 5])
            .on("zoom", function () {
                const t = d3.event.transform;
                lastTransformRef.current = t;
                zoomGroup.attr("transform", t);
                overlayGroup.attr("transform", t); // keep overlay aligned
            });

        svg.call(zoom);

        if (!selecteddata) {
            svg.call(zoom.transform, lastTransformRef.current);
        }

        const nodeGroup = zoomGroup.selectAll("g")
            .data(nodes)
            .enter()
            .append("g")
            .style("cursor", d => (d.empty ? "default" : "pointer"))
            .each(function (d, i) {
                if (canvasRefs?.current) canvasRefs.current[i] = this;
            })
            .on("click", function (d) {
                if (!d.isCenter && !d.empty) {
                    nodeclick(d.id);
                }
                else if (d.id === "center_image") {
                    window.open(sharedproperties["SCATTER"].redirects[0], "_blank");
                }
            });

        nodeGroup.each(function (d) {
            const el = d3.select(this);

            if (d.isCenter) {

                const size = d.r * 2;

                el.append("image")
                    .attr("xlink:href", img)
                    .attr("x", -size / 2)
                    .attr("y", -size / 2)
                    .attr("width", size)
                    .attr("height", size)
                    .attr("clip-path", "url(#clip-center)")
                    .attr("preserveAspectRatio", "xMidYMid slice");

            }
            else if (!d.empty && imagesCache.current[d.id]) { // i want the path to be in the center of the rect.

                let constant

                if (selecteddata && selecteddata.addr === d.id) { // selected, draw the doodle ONLY

                    const size = d.r * 5;

                    const defs = svg.append("defs");

                    const pattern = defs.append("pattern")
                        .attr("id", "asciiDots")
                        .attr("patternUnits", "userSpaceOnUse")
                        .attr("width", 10)
                        .attr("height", 10);

                    pattern.append("circle")
                        .attr("cx", 1)
                        .attr("cy", 1)
                        .attr("r", 0.7)
                        .attr("fill", dotcolor)
                        .attr("opacity", 0.4);

                    el.raise();

                    // base card
                    el.append("rect")
                        .attr("x", (-size / 2) - 5)
                        .attr("y", (-size / 2) - 5)
                        .attr("width", size)
                        .attr("height", size)
                        .attr("rx", 100)
                        // .attr("fill", bgcolor)
                        .attr('fill', 'transparent')
                        .style("vector-effect", "non-scaling-stroke")
                        .style("paint-order", "stroke")
                        .style("pointer-events", "none");

                    // dots overlay
                    el.append("rect")
                        .attr("x", (-size / 2) - 5)
                        .attr("y", (-size / 2) - 5)
                        .attr("width", size)
                        .attr("height", size)
                        .attr("rx", 100)
                        .attr("fill", "url(#asciiDots)")
                        .style("pointer-events", "none");

                    // show path
                    const { pathsarray } = selecteddata || {};

                    if (!pathsarray) return;

                    const bounds = getBounds(pathsarray);

                    const drawWidth = bounds.maxX - bounds.minX;
                    const drawHeight = bounds.maxY - bounds.minY;

                    const centerX = bounds.minX + drawWidth / 2;
                    const centerY = bounds.minY + drawHeight / 2;
                    const padding = 20;

                    const available = size - padding;
                    const scaleFactor = available / Math.max(drawWidth, drawHeight);

                    const drawGroup = el.append("g")
                        .attr("transform", `
                            scale(${scaleFactor})
                            translate(${-centerX}, ${-centerY})
                        `);

                    let pathIndex = 0;

                    function drawNextPath() {
                        const stroke = pathsarray[pathIndex];
                        const d = pointsToPath(stroke.points);

                        const path = drawGroup.append("path")
                            .attr("d", d)
                            .attr("fill", "none")
                            .attr("stroke", stroke.color)
                            .attr("stroke-width", guestbook_doodle_strokewt)
                            .attr("stroke-linecap", "round")
                            .attr("stroke-linejoin", "round")

                        const len = path.node().getTotalLength();

                        path
                            .attr("stroke-dasharray", len)
                            .attr("stroke-dashoffset", len)
                            .transition()
                            .duration(200) // speed of drawing
                            .ease(d3.easeLinear)
                            .attr("stroke-dashoffset", 0)
                            .on("end", () => {
                                pathIndex++;
                                if (pathIndex >= pathsarray.length) {
                                    setTimeout(() => { // restart after small pause
                                        drawGroup.selectAll("*").remove();
                                        pathIndex = 0;
                                        drawNextPath();
                                    }, 1000);
                                }
                                else drawNextPath();
                            });
                    }

                    drawNextPath();

                    // panToNode(svg, d, vbwidth, vbheight, lastTransformRef, zoom);

                }
                else { // unselected

                    const size = d.r * 1.3;

                    el.append("image")
                        .attr("xlink:href", imagesCache.current[d.id])
                        .attr("x", -size / 2)
                        .attr("y", -size / 2)
                        .attr("width", size)
                        .attr("height", size)
                        .attr('opacity', 0.5)
                        .attr("clip-path", constant > 1.3 ? "url(#clip-center)" : null)
                        .attr("preserveAspectRatio", "xMidYMid slice")
                        .attr("rx", 15)

                }

            }
        });













        // 1. Create a Group to hold both the SVG Rect and the HTML foreignObject
        // This allows us to move both elements simultaneously if needed, 
        // though here we update their x/y individually for precision.
        let overlayGroupContainer = selecteddata
            ? overlayGroup.append("g")
                .style("pointer-events", "none")
            : null;

        // 2. The Background Rect 
        // This handles the "physical" look: the stroke (border) and the fill (background)
        let backgroundRect = overlayGroupContainer
            ? overlayGroupContainer.append("rect")
                .attr("fill", bgcolor)
                .attr("stroke", framebordercolor)
                .attr("stroke-width", framestrokewt)
                .attr("rx", 15) // Matches the rounded look of your original div
                .style("vector-effect", "non-scaling-stroke") // Add this!
            : null;

        // 3. The foreignObject 
        // This acts as the container for HTML content inside the SVG coordinate space
        let overlayRect = overlayGroupContainer
            ? overlayGroupContainer.append("foreignObject")
            : null;

        // 4. The HTML Content Div
        // We removed the border here since backgroundRect now handles it
        let overlayDiv = overlayRect
            ? overlayRect.append("xhtml:div")
                .style("width", "100%")
                .style("height", "100%")
                .style("box-sizing", "border-box") // Vital: keeps padding inside the rect
                .style("color", "var(--maindark)")
                .style("padding", "0.5em")
                .style("display", "flex")
                .style("flex-direction", "column")
            : null;

        if (overlayDiv) {
            const imageWrapper = selecteddata.img && selecteddata.img.length > 0
                ? overlayDiv.append("div")
                    .style("width", "100%")
                    .style("height", "60%")
                    .style("background", "black")
                    .style("display", "flex")
                    .style("align-items", "center")
                    .style("justify-content", "center")
                    .style("flex-shrink", "0")
                    .style('border-radius', '12px') // Slightly smaller than the outer rect
                : null;

            if (imageWrapper) {
                imageWrapper.append("img")
                    .attr("src", S3CLOUD_CLOUDFRONT + selecteddata.img)
                    .style("max-width", "100%")
                    .style("max-height", "100%")
                    .style("padding", "15px")
                    .style("object-fit", "contain")
                    .style("display", "block")
                    .style('border-radius', '12px');
            }

            const textWrapper = overlayDiv.append("div")
                .style("width", "100%")
                .style("height", "auto")
                .style("padding", "10px 0px")
                .style("white-space", "pre-line");

            textWrapper.text(selecteddata.msg);

            const nameWrapper = overlayDiv.append("div")
                .style("width", "100%")
                .style("height", "5%")
                .style("display", "flex")
                .style("place-items", "flex-end")
                .style("justify-content", "flex-end")
                .style("text-align", "center")
                .style("position", "relative");

            nameWrapper.text(selecteddata.name);
        }



        // Pre-calculate the selected node
        const selectedNode = selecteddata ? nodes.find(n => n.id === selecteddata.addr) : null;

        const simulation = d3.forceSimulation(nodes)
            .force("center", d3.forceCenter(vbwidth / 2, vbheight / 2))
            .force("collision", d3.forceCollide().radius(d =>
                d.id === selecteddata?.addr ? d.r * 2.5 : d.r + collideconstant
            ))
            .force("charge", d3.forceManyBody().strength(forcestrength))
            .alphaDecay(0.05)
            .velocityDecay(0.6)
            .on("tick", () => {
                if (simulation.alpha() < 0.001) {
                    simulation.stop();
                }

                // Pan after simulation settles
                if (selectedNode) {
                    // On first render, wait for coordinates to be set
                    if (isFirstRenderRef.current) {
                        isFirstRenderRef.current = false;
                        // Give a small delay to ensure coordinates are finalized
                        setTimeout(() => {
                            panToNode(svg, selectedNode, vbwidth, vbheight, lastTransformRef, zoom);
                        }, 100);
                    } else {
                        panToNode(svg, selectedNode, vbwidth, vbheight, lastTransformRef, zoom);
                    }
                }


                // panToNode(svg, d, vbwidth, vbheight, lastTransformRef, zoom);


                // Update Node Positions
                nodeGroup.attr("transform", d => {
                    d.x = Math.max(d.r, Math.min(vbwidth - d.r, d.x));
                    d.y = Math.max(d.r, Math.min(vbheight - d.r, d.y));
                    return `translate(${d.x},${d.y})`;
                });

                // Update Overlay Position (Rect + ForeignObject)
                if (overlayGroupContainer && selectedNode) {
                    const r = selectedNode.r;
                    const rectWidth = r * 20;
                    const rectHeight = r * 17.1;
                    const xOffset = (r * 2) + 20;

                    const targetX = selectedNode.x + xOffset;
                    const targetY = selectedNode.y - (rectHeight / 2);

                    // Update SVG Rect
                    backgroundRect
                        .attr("width", rectWidth)
                        .attr("height", rectHeight)
                        .attr("x", targetX)
                        .attr("y", targetY);

                    // Update ForeignObject to overlay exactly on the Rect
                    overlayRect
                        .attr("width", rectWidth)
                        .attr("height", rectHeight)
                        .attr("x", targetX)
                        .attr("y", targetY)
                        .style("visibility", "visible");
                }
            });

        return () => simulation.stop();

    }, [nodes, dim, width, height, img, selecteddata]);

    return (
        <svg
            ref={svgRef}
            viewBox={`0 0 ${vbwidth} ${vbheight}`}
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            style={{ width: width, height: height, display: "block", touchAction: "none" }}
        />
    );
}

function GuestScatterChart({
    guestdata,
    guestdataaddr,
    maxguestnum,
    dim,
    height,
    width,
    nodeclick,
    canvasRefs,
    properties,
    sharedproperties,
    selecteddata
}) {

    const [img, setImg] = useState("");
    const svgRef = useRef();
    const imagesCache = useRef({});
    const lastTransformRef = useRef(d3.zoomIdentity);

    const vbwidth = 800;
    const vbheight = 600;

    useEffect(() => {
        if (sharedproperties && sharedproperties["SCATTER"] && sharedproperties["SCATTER"].allowimg && sharedproperties["SCATTER"].img.length > 0) {
            setImg(S3CLOUD_CLOUDFRONT + sharedproperties["SCATTER"].img);
        }
    }, [sharedproperties, properties]);

    const nodes = useMemo(() => {
        if (!guestdataaddr) return [];
        if (img.length > 0) {
            return [
                { id: "center_image", r: dim, isCenter: true, fx: vbwidth / 2, fy: vbheight / 2 },
                ...Array.from({ length: Number(maxguestnum) }, (_, i) => {
                    const addr = guestdataaddr[i];
                    return addr
                        ? { id: addr, r: dim / 2, guest: guestdata[addr], isCenter: false }
                        : { id: "empty_" + i, r: 0, isCenter: false, empty: true };
                })
            ];
        }
        else {
            return [
                ...Array.from({ length: Number(maxguestnum) }, (_, i) => {
                    const addr = guestdataaddr[i];
                    return addr
                        ? { id: addr, r: dim / 2, guest: guestdata[addr], isCenter: false }
                        : { id: "empty_" + i, r: 0, isCenter: false, empty: true };
                })
            ];
        }
    }, [guestdataaddr, maxguestnum, dim, guestdata, img]);

    useEffect(() => {
        if (!guestdataaddr || !svgRef.current) return;

        guestdataaddr.forEach((id) => {
            const guest = guestdata[id];
            if (guest?.image && !imagesCache.current[id]) {
                imagesCache.current[id] = guest.image;
            }
        });

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const defs = svg.append("defs");

        defs.append("clipPath")
            .attr("id", "clip-center")
            .append("circle")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", dim);

        const zoomGroup = svg.append("g");      // Doodles layer

        const zoom = d3.zoom()
            .scaleExtent([0.5, 5])
            .on("zoom", function () {
                const t = d3.event.transform;
                lastTransformRef.current = t;
                zoomGroup.attr("transform", t);
            });

        svg.call(zoom);

        if (!selecteddata) {
            svg.call(zoom.transform, lastTransformRef.current);
        }

        const nodeGroup = zoomGroup.selectAll("g")
            .data(nodes)
            .enter()
            .append("g")
            .style("cursor", d => (d.empty ? "default" : "pointer"))
            .each(function (d, i) {
                if (canvasRefs?.current) canvasRefs.current[i] = this;
            })
            .on("click", function (d) {
                if (!d.isCenter && !d.empty) {
                    nodeclick(d.id);
                }
                else if (d.id === "center_image") {
                    window.open(sharedproperties["SCATTER"].redirects[0], "_blank");
                }
            });

        nodeGroup.each(function (d) {
            const el = d3.select(this);

            if (d.isCenter) {
                const size = d.r * 2;
                el.append("image")
                    .attr("xlink:href", img)
                    .attr("x", -size / 2)
                    .attr("y", -size / 2)
                    .attr("width", size)
                    .attr("height", size)
                    .attr("clip-path", "url(#clip-center)")
                    .attr("preserveAspectRatio", "xMidYMid slice");
            }
            else if (!d.empty && imagesCache.current[d.id]) {

                let size = d.r * 1.3
                let clippath = "url(#clip-center)"

                if (selecteddata && selecteddata.addr === d.id) {
                    size = d.r * 3
                    clippath = ''
                }

                el.append("image")
                    .attr("xlink:href", imagesCache.current[d.id])
                    .attr("x", -size / 2)
                    .attr("y", -size / 2)
                    .attr("width", size)
                    .attr("height", size)
                    .attr("clip-path", clippath)
                    .attr("preserveAspectRatio", "xMidYMid slice");
            }
        });

        const simulation = d3.forceSimulation(nodes)
            .force("center", d3.forceCenter(vbwidth / 2, vbheight / 2))
            .force("collision", d3.forceCollide().radius(d => d.r + collideconstant))
            .force("charge", d3.forceManyBody().strength(forcestrength))
            .alphaDecay(0.05)
            .velocityDecay(0.6) // More friction = settles faster
            .on("tick", () => {
                nodeGroup.attr("transform", d => `translate(${d.x},${d.y})`);
            })
            .on("end", () => simulation.stop());


        return () => simulation.stop();

    }, [nodes, dim, width, height, img]);

    // Add separate effect to update selected node appearance
    useEffect(() => {
        if (!selecteddata || !svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("g image").each(function (d) {
            if (d && !d.isCenter && !d.empty) {
                const isSelected = selecteddata.addr === d.id;
                const size = isSelected ? d.r * 3 : d.r * 1.3;
                const clippath = isSelected ? '' : 'url(#clip-center)';

                d3.select(this)
                    .transition()
                    .duration(300)
                    .attr("x", -size / 2)
                    .attr("y", -size / 2)
                    .attr("width", size)
                    .attr("height", size)
                    .attr("clip-path", clippath);
            }
        });
    }, [selecteddata]);

    return (
        <svg
            ref={svgRef}
            viewBox={`0 0 ${vbwidth} ${vbheight}`}
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            style={{
                width: width,
                height: height,
                display: "block",
                touchAction: "none"
            }}
        />
    );
}

const OwnerArcTitle = (props) => {
    const {
        device,
        default_svg_properties, titledata,
        ownertitletopbool, updateOwnertitletopbool,
        ownersubtitletopbool, updateOwnersubtitletopbool,
        updatetitledata, updateTitletext,
        updatesubtitledata, updateSubtitletext,
        backuptitle, backupsubtitle,
        savetitle, savesubtitle,
        canceltitle, cancelsubtitle,
        showgrid,
        panelPosition,
        updatePanelAppearPosition
    } = props

    return <div style={{
        display: 'flex', position: 'relative', flexDirection: 'column', placeSelf: 'center',
        height: default_svg_properties[device].height,
        width: default_svg_properties[device].width,
    }}>

        <svg
            width={default_svg_properties[device].width}
            height={default_svg_properties[device].height}
            viewBox={"0 0 " + default_svg_properties[device].width + " " + default_svg_properties[device].height}
        >

            <defs>
                <path
                    id="topArc"
                    fill="transparent"
                    d={`
                        M 
                        ${titledata['title'][device]['style']['arc'].startX}, 
                        ${titledata['title'][device]['style']['arc'].startY} 
                        A 
                        ${titledata['title'][device]['style']['arc'].rx}, 
                        ${titledata['title'][device]['style']['arc'].ry} 
                        ${titledata['title'][device]['style']['arc']['x-axis-rotation']} 
                        ${titledata['title'][device]['style']['arc']['large-arc-flag']} 
                        ${titledata['title'][device]['style']['arc']['sweep-flag']} 
                        ${titledata['title'][device]['style']['arc'].endX}, 
                        ${titledata['title'][device]['style']['arc'].endY}
                        `}
                />
                <path
                    id="bottomArc"
                    d={`
                        M
                        ${titledata['subtitle'][device]['style']['arc'].startX},  
                        ${titledata['subtitle'][device]['style']['arc'].startY} 
                        A 
                        ${titledata['subtitle'][device]['style']['arc'].rx}, 
                        ${titledata['subtitle'][device]['style']['arc'].ry} 
                        ${titledata['subtitle'][device]['style']['arc']['x-axis-rotation']} 
                        ${titledata['subtitle'][device]['style']['arc']['large-arc-flag']} 
                        ${titledata['subtitle'][device]['style']['arc']['sweep-flag']} 
                        ${titledata['subtitle'][device]['style']['arc'].endX}, 
                        ${titledata['subtitle'][device]['style']['arc'].endY}
                    `}
                    fill="transparent"
                />
            </defs>

            <text
                fontSize={titledata['title'][device]['style']['arc'].fontSize}
                fill={titledata['title'][device]['style']['arc'].color}
                fontFamily={titledata['title'][device]['style']['arc'].fontFamily}
                dy={titledata['title'][device]['style']['arc'].dy}
                transform={`scale(${titledata['title'][device]['style']['arc'].scale})`}
                onClick={(e) => {
                    updatePanelAppearPosition(e)
                    updateOwnertitletopbool(e)
                    backuptitle(titledata['title'][device]['style']['arc'], device)
                }}
                style={{ cursor: 'pointer', width: titledata['title'][device]['style']['arc'].width }}
            >
                <textPath href="#topArc" startOffset="50%" textAnchor="middle">
                    {titledata['title']['text']}
                </textPath>
            </text>

            <text
                fontSize={titledata['subtitle'][device]['style']['arc'].fontSize}
                fill={titledata['subtitle'][device]['style']['arc'].color}
                fontFamily={titledata['subtitle'][device]['style']['arc'].fontFamily}
                dy={titledata['subtitle'][device]['style']['arc'].dy}
                transform={`scale(${titledata['subtitle'][device]['style']['arc'].scale})`}

                onClick={(e) => {
                    updatePanelAppearPosition(e)
                    updateOwnersubtitletopbool(e)
                    backupsubtitle(titledata['subtitle'][device]['style']['arc'], device)
                }}
                style={{ cursor: 'pointer', width: titledata['subtitle'][device]['style']['arc'].width }}
            >
                <textPath href="#bottomArc" startOffset="50%" textAnchor="middle">
                    {titledata['subtitle']['text']}
                </textPath>
            </text>


            {/* Vertical center line */}
            {showgrid && <line
                x1={default_svg_properties[device].width / 2}
                y1={0}
                x2={default_svg_properties[device].width / 2}
                y2={default_svg_properties[device].height}
                stroke="#7c7c7c"
                strokeWidth={1}
                strokeDasharray="4 2"  // ← makes it dashed
            />}

            {/* Horizontal center line */}
            {showgrid && <line
                x1={0}
                y1={default_svg_properties[device].height / 2}
                x2={default_svg_properties[device].width}
                y2={default_svg_properties[device].height / 2}
                stroke="#7c7c7c"
                strokeWidth={1}
                strokeDasharray="4 2"  // ← makes it dashed
            />}

            {/* SVG Border */}
            {showgrid && <rect
                x={0}
                y={0}
                width={default_svg_properties[device].width}
                height={default_svg_properties[device].height}
                fill="none"
                stroke="#7c7c7c"
                strokeWidth={1}
                strokeDasharray="4 2"  // ← makes it dashed
            />}

        </svg>

    </div>

}

function Guestbooklookup(props) {
    const ht = ScreenHeight()

    const [titledata, setTitledata] = useState({})
    const [properties, setProperties] = useState({})
    const [sharedproperties, setSharedproperties] = useState({})
    const [guestdataaddr, setGuestdataaddr] = useState([])
    const [guestdata, setGuestdata] = useState([])
    const [maxguestnum, setMaxguestnum] = useState(null)
    const [lookuptype, setLookuptype] = useState('SCATTER')// GRID, SCATTER

    // Background color
    const [bgcolor, setBgcolor] = useState(null)

    const [readyToDraw, setReadyToDraw] = useState(0); // 0 : not ready, 1 : guest data ready, 2: draw

    // Current select data related
    const [selecteddata, setSelecteddata] = useState(null) // object.keys [ "name", "msg", "paths", "img", "pathstatus", "imgstatus", "pathsarray" ]
    const [showportal, setShowportal] = useState(-1) // -1 = null, 0 = false, 1 = true

    const [fullscreen, setFullscreen] = useState(false)

    const containerRef = useRef(null);

    const canvasRefs = useRef([]);
    const portalcanvaRef = useRef(null)

    const intervalRef = useRef(null);
    const isManualSelectionRef = useRef(false);

    const device = Checkdevice()

    const default_svg_properties = (device && device.length > 0)
        ? {
            'WEB': { width: 400, height: 150, startX: 10, startY: 80, endX: 290, endY: 80 },
            'TABLET': { width: 350, height: 150, },
            'MOBILE': { width: 300, height: 150, },
            'PRINT': { width: 1748, height: 2480, startX: 10, startY: 80, endX: 290, endY: 80 },
        }
        : {}

    useEffect(() => {
        props.hidetopbar(true);
    }, [props])

    useEffect(() => {
        if (lookuptype === 'GRID') {

        }
        else {
            let previousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden'
            return () => {
                document.body.style.overflow = previousOverflow || '';
            };
        }


    }, [lookuptype])

    useEffect(() => {

        const { urlhandler, modpasscode } = props.match.params

        axios({
            method: 'POST',
            url: READ_DRAWBOOKLOOKUP,
            data: { urlhandler, modpasscode }
        })
            .then(async (item) => {
                const { message, result } = item.data
                if (message === 'SUCCESS') {

                    const {
                        urlhandler, passcodemod, dbtype,
                        maxguestnum, bgcolor, fontcolor,
                        guestdataaddr, guestpages, titledata,
                        properties, sharedproperties, gtpp, guestdata, purchase,
                        lookuptype
                    } = result

                    setLookuptype(lookuptype)
                    setTitledata(titledata)
                    setProperties(properties)
                    setSharedproperties(sharedproperties)

                    function expandguestdataTo300(data) {
                        const result = { ...data };                          // keep original
                        const keys = Object.keys(data);                      // original UUIDs
                        const targetCount = 300;

                        // If already >= 300, just return first 300
                        if (keys.length >= targetCount) return Object.fromEntries(
                            keys.slice(0, targetCount).map(k => [k, result[k]])
                        );

                        // Helper for generating UUIDs
                        const genId = () => crypto.randomUUID();

                        let i = keys.length;
                        let loopIndex = 0;

                        while (i < targetCount) {
                            const originalId = keys[loopIndex % keys.length];  // rotate through originals
                            const newId = genId();

                            result[newId] = {
                                ...data[originalId],
                                duplicateOf: originalId,  // (optional: mark original source)
                            };

                            i++;
                            loopIndex++;
                        }
                        return result;
                    }


                    let local_guestdata
                    let local_guestdataaddr

                    if (false) {
                        local_guestdata = expandguestdataTo300(guestdata)
                        local_guestdataaddr = Object.keys(local_guestdata)

                        for (let i = 0; i < local_guestdataaddr.length; i++) {
                            const idx = local_guestdataaddr[i];
                            const item = local_guestdata[idx];
                            if (item && typeof item.paths === 'string') {
                                try {
                                    const fullURL = S3CLOUD_CLOUDFRONT + item.paths;

                                    const response = await fetch(fullURL);
                                    if (!response.ok) throw new Error(`HTTP ${response.status}`);

                                    const arrayBuffer = await response.arrayBuffer(); // ✅ binary, not text
                                    const compressed = new Uint8Array(arrayBuffer);   // ✅ no atob

                                    const jsonString = pako.ungzip(compressed, { to: 'string' }); // ✅ decompress
                                    const parsedPaths = JSON.parse(jsonString);                   // ✅ parse JSON

                                    // guestdata[idx].paths = parsedPaths;
                                    local_guestdata[idx].pathsarray = parsedPaths;

                                    console.log(`✅ Decompressed paths for index ${idx}`);

                                }
                                catch (error) {
                                    console.error(`❌ Failed to decompress paths for key ${idx}`, error);
                                    guestdata[idx].paths = '';
                                }
                            }
                        }

                    }
                    else {
                        local_guestdata = JSON.parse(JSON.stringify(guestdata))
                        local_guestdataaddr = guestdataaddr.slice()

                        for (let i = 0; i < local_guestdataaddr.length; i++) {
                            const idx = local_guestdataaddr[i];

                            const item = local_guestdata[idx];
                            if (item && typeof item.paths === 'string') {
                                try {
                                    const fullURL = S3CLOUD_CLOUDFRONT + item.paths;

                                    const response = await fetch(fullURL);
                                    if (!response.ok) throw new Error(`HTTP ${response.status}`);

                                    const arrayBuffer = await response.arrayBuffer(); // ✅ binary, not text
                                    const compressed = new Uint8Array(arrayBuffer);   // ✅ no atob

                                    const jsonString = pako.ungzip(compressed, { to: 'string' }); // ✅ decompress
                                    const parsedPaths = JSON.parse(jsonString);                   // ✅ parse JSON

                                    // guestdata[idx].paths = parsedPaths;
                                    local_guestdata[idx].pathsarray = parsedPaths;

                                }
                                catch (error) {
                                    guestdata[idx].paths = '';
                                }
                            }
                        }

                    }


                    if (PIXELATED) {


                        Object.keys(local_guestdata).forEach(async (key) => {
                            const guest = local_guestdata[key];

                            if (guest.pathsarray && guest.pathsarray.length > 0) {
                                const img = await pathsToImage(guest.pathsarray, 140);

                                // Convert base64 → Image object
                                const imageObj = new Image();
                                imageObj.src = img;

                                imageObj.onload = () => {
                                    guest.image = pixelateImage(imageObj, 4); // pixel size 10
                                };
                            } else {
                                guest.image = null;
                            }
                        });

                    }
                    else {

                        // Convert paths to images for all guests
                        Object.keys(local_guestdata).forEach((key) => {
                            const guest = local_guestdata[key];
                            if (guest.pathsarray && guest.pathsarray.length > 0) {
                                // Generate image from paths
                                guest.image = pathsToImage(guest.pathsarray, 140); // 400px canvas, adjust if needed

                            }
                            else {
                                guest.image = { color: "", points: [] }; // fallback
                            }
                        });


                    }

                    console.log('local_guestdata', local_guestdata)
                    setGuestdata({ ...local_guestdata });
                    setGuestdataaddr(local_guestdataaddr || [])
                    setMaxguestnum(maxguestnum)
                    setBgcolor(bgcolor)
                }
            })
            .catch((e) => {
                // props.history.push('/') 
            })


    }, [])

    useEffect(() => {
        if (
            guestdataaddr.length > 0 &&
            Object.keys(guestdata).length > 0 &&
            maxguestnum !== null
        ) {
            setTimeout(() => setReadyToDraw(1), 0); // let DOM render
        }
    }, [guestdataaddr, guestdata, maxguestnum]);

    useEffect(() => {
        if (selecteddata && portalcanvaRef && portalcanvaRef.current && showportal === 1) {
            const canvas = portalcanvaRef.current

            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            (selecteddata.pathsarray || []).forEach(({ color = 'black', points }) => {
                const pts = points;
                if (!pts || pts.length < 2) return;
                ctx.beginPath();
                ctx.strokeStyle = color;
                ctx.lineWidth = 8;
                ctx.moveTo(pts[0].x, pts[0].y);
                for (let i = 1; i < pts.length; i++) {
                    ctx.lineTo(pts[i].x, pts[i].y);
                }
                ctx.stroke();

            });
        }
    }, [selecteddata, showportal])

    useLayoutEffect(() => {
        if (readyToDraw === 0 || readyToDraw === 2) return;

        const allReady = guestdataaddr.every((_, i) => canvasRefs.current[i]);
        if (!allReady) return;

        const drawPath = (ctx, pts, color, pointsPerFrame = 5) => {
            return new Promise((resolve) => {
                ctx.beginPath();
                ctx.strokeStyle = color;
                ctx.lineWidth = 8;
                ctx.moveTo(pts[0].x, pts[0].y);

                let i = 1;
                const stepDraw = () => {
                    if (i >= pts.length) {
                        ctx.stroke();
                        resolve();
                        return;
                    }
                    // Draw multiple points per frame for speed-up
                    for (let count = 0; count < pointsPerFrame && i < pts.length; count++, i++) {
                        ctx.lineTo(pts[i].x, pts[i].y);
                    }
                    ctx.stroke();
                    requestAnimationFrame(stepDraw);
                };

                stepDraw();
            });
        };

        const drawAllPathsForCanvas = async (ctx, pathsarray) => {
            for (const { color = 'black', points } of pathsarray) {
                const pts = points;
                if (!pts || pts.length < 2) continue;
                await drawPath(ctx, pts, color, 10); // 10 points per frame = faster
            }
        };

        const drawPromises = guestdataaddr.map((id, index) => {
            const canvas = canvasRefs.current[index];
            let guest = guestdata[id];
            if (!canvas || !guest?.pathsarray) {
                console.warn(`⛔ Skipping canvas ${index}:`, { canvas, guest });
                return Promise.resolve();
            }
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            return drawAllPathsForCanvas(ctx, guest.pathsarray);
        });

        Promise.all(drawPromises).then(() => {
            setReadyToDraw(2);
        });
    }, [readyToDraw, lookuptype]);

    useLayoutEffect(() => {
        const originalStyle = window.getComputedStyle(document.body).backgroundColor;
        document.body.style.backgroundColor = bgcolor;
        return () => (document.body.style.backgroundColor = originalStyle);
    }, [bgcolor]);

    useEffect(() => {
        const onChange = () => {
            setFullscreen(!!document.fullscreenElement)
            setSelecteddata(null)
        };
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);











    // Single function to clear and restart interval
    const resetInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(() => {
            setSelecteddata((selecteddata) => {
                let index = 0;
                if (selecteddata && selecteddata.addr) {
                    index = guestdataaddr.indexOf(selecteddata.addr);
                }

                if (guestdataaddr.length !== index + 1) {
                    const addr = guestdataaddr[index + 1];
                    return { ...guestdata[addr], addr: addr };
                } else {
                    const addr = guestdataaddr[0];
                    return { ...guestdata[addr], addr: addr };
                }
            });
        }, 10000);
    }, [guestdata, guestdataaddr]);

    // Separate function to handle manual selection
    const handleManualSelect = (addr) => {
        setSelecteddata({ ...guestdata[addr], addr: addr });

        if (fullscreen) {
            isManualSelectionRef.current = true;
            resetInterval(); // Use resetInterval instead
        }
    };

    const goright = () => {
        if (!selecteddata || !selecteddata.addr) return;

        let index = guestdataaddr.indexOf(selecteddata.addr);

        if (guestdataaddr.length !== (index + 1)) {
            const addr = guestdataaddr[index + 1];
            setSelecteddata({ ...guestdata[addr], addr: addr });
        } else {
            const addr = guestdataaddr[0];
            setSelecteddata({ ...guestdata[addr], addr: addr });
        }
        if (fullscreen) resetInterval();
    };

    const goleft = () => {
        if (!selecteddata || !selecteddata.addr) return;

        let index = guestdataaddr.indexOf(selecteddata.addr);

        if ((index - 1) === -1) {
            const addr = guestdataaddr[guestdataaddr.length - 1];
            setSelecteddata({ ...guestdata[addr], addr: addr });
        }
        else {
            const addr = guestdataaddr[index - 1];
            setSelecteddata({ ...guestdata[addr], addr: addr });
        }
        if (fullscreen) resetInterval();
    };

    useEffect(() => {
        if (!fullscreen) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            return;
        }

        // Set to first address immediately when entering fullscreen
        if (guestdataaddr.length > 0) {
            const firstAddr = guestdataaddr[0];
            setSelecteddata({ ...guestdata[firstAddr], addr: firstAddr });
        }

        resetInterval();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [fullscreen, guestdata, guestdataaddr, resetInterval]);




















    // Add keyboard event listener
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'ArrowLeft') {
                goleft();
            } else if (event.key === 'ArrowRight') {
                goright();
            }
        };

        window.addEventListener('keydown', handleKeyPress);

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [selecteddata, guestdataaddr, guestdata]); // Dependencies needed for goleft/goright




    const closeshowportal = () => {
        setShowportal(0)
        portalcanvaRef.current = null
    }

    const MOBILE_PORTAL_CONTENT = <div className="Guestbooklookup_portalroot" style={{ height: 600, width: 'calc( 100vw - 1em )' }}>

        <button
            className='Guestbooklookup_close'
            onClick={closeshowportal}
        >
            {iconwrapper(CROSS_ICON, { width: 20, height: 20, display: 'flex', placeContent: 'center', placeItems: 'center', touchAction: 'manipulation' })}
        </button>

        <div
            className="Guestbooklookup_spriteroot"
            style={{
                height: dim * 1.3,
                width: dim * 1.3,
                position: 'absolute',
                left: `calc( 50% - ${(dim * 1.3) / 2}px )`,
                borderRadius: '100% 100% 0px 0px',
                overflow: 'hidden',
                top: -(dim * 1.3) / 3
            }}
        >
            <canvas
                ref={portalcanvaRef}
                width={300}
                height={300}
                style={{
                    // transform: `scale(${dim / 300})`,
                    // transformOrigin: 'top left',
                    display: 'block',
                    padding: dim * 0.15,
                    backgroundColor: bgcolor
                }}
            />
        </div>

        <div style={{ height: 600 - (dim * 1.3) + (dim * 1.3) / 3, width: '100%', position: 'absolute', bottom: 0 }}>

            {(selecteddata && selecteddata.img)
                ? <div style={{}}>
                    <img
                        className="Guestbooklookup_img"
                        src={S3CLOUD_CLOUDFRONT + selecteddata.img}
                        crossOrigin="anonymous"
                        width={'100%'}
                        height={250}
                    />
                </div>
                : null}

            {(selecteddata && selecteddata.msg && selecteddata.img.length > 0)
                ? <div
                    className="Guestbooklookup_txt"
                    style={{
                        padding: 10,
                        height: 'calc( 100% - 250px - 40px )',
                        overflowY: 'auto'
                    }}
                >
                    {selecteddata.msg}
                </div>
                : null}

            {selecteddata && selecteddata.msg && selecteddata.img.length === 0
                ? <div
                    className="Guestbooklookup_txt"
                    style={{
                        padding: 10,
                        height: 'calc( 100% - 40px )',
                        overflowY: 'auto'
                    }}
                >
                    {selecteddata.msg}
                </div>
                : null}


            {(selecteddata && selecteddata.name)
                ? <div
                    className="Guestbooklookup_name"
                    style={{ padding: 10, display: 'flex', position: 'absolute', bottom: 0, height: 40 }}
                >
                    {selecteddata.name}
                </div>
                : null}
        </div>

        <div style={{ display: 'flex', flexDirection: 'row', position: 'absolute', bottom: -40, right: 0, touchAction: 'manipulation' }}>
            <button className="Guestbooklookup_mobileleft" onClick={goleft}>
                {iconwrapper(LEFTARROW_ICON_SMALL, { color: bgcolor, width: 25, height: 25, display: 'flex', placeContent: 'center', placeItems: 'center', touchAction: 'manipulation' })}
            </button>
            <button className="Guestbooklookup_mobileright" onClick={goright}>
                {iconwrapper(RIGHTARROW_ICON_SMALL, { color: bgcolor, width: 25, height: 25, display: 'flex', placeContent: 'center', placeItems: 'center', touchAction: 'manipulation' })}
            </button>
        </div>

    </div>

    const WEB_PORTAL_CONTENT = <div className="Guestbooklookup_portalroot">

        <button
            className='Guestbooklookup_close'
            onClick={closeshowportal}
        >
            {iconwrapper(CROSS_ICON, { width: 20, height: 20, display: 'flex', placeContent: 'center', placeItems: 'center' })}
        </button>

        <button className="Guestbooklookup_left" onClick={goleft}>
            {iconwrapper(LEFTARROW_ICON_SMALL, { color: bgcolor, width: 25, height: 25, display: 'flex', placeContent: 'center', placeItems: 'center', })}
        </button>

        <button className="Guestbooklookup_right" onClick={goright}>
            {iconwrapper(RIGHTARROW_ICON_SMALL, { color: bgcolor, width: 25, height: 25, display: 'flex', placeContent: 'center', placeItems: 'center', })}
        </button>

        <div
            className="Guestbooklookup_spriteroot"
            style={{
                height: dim * 1.3,
                width: dim * 1.3,
                position: 'absolute',
                top: -1 * ((dim * 1.3) / 2) + 'px',
                left: `calc( 50% - ${(dim * 1.3) / 2}px )`,
                borderRadius: '100% 100% 0px 0px',
                overflow: 'hidden'
            }}>
            <canvas
                ref={portalcanvaRef}
                width={300}
                height={300}
                style={{
                    // transform: `scale(${dim / 300})`,
                    // transformOrigin: 'top left',
                    display: 'block',
                    padding: dim * 0.15,
                    backgroundColor: bgcolor
                }}
            />
        </div>

        {(selecteddata && selecteddata.img)
            ? <div style={{ padding: (dim * 1.35) / 2, paddingBottom: (dim * 1.35) / 6 }}>
                <img
                    className="Guestbooklookup_img"
                    src={S3CLOUD_CLOUDFRONT + selecteddata.img}
                    crossOrigin="anonymous"
                    width={'100%'}
                    height={350}
                />
            </div>
            : null}

        {(selecteddata && selecteddata.msg && selecteddata.img.length > 0)
            ? <div
                className="Guestbooklookup_txt"
                style={{
                    padding: (dim * 1.35) / 2,
                    paddingTop: 0,
                    height: 170
                }}
            >
                {selecteddata.msg}
            </div>
            : null}

        {selecteddata && selecteddata.msg && selecteddata.img.length === 0
            ? <div
                className="Guestbooklookup_txt"
                style={{
                    padding: (dim * 1.35) / 2,
                    // paddingBottom:((dim * 1.35) / 2) + ( (dim * 1.35) / 6),
                    height: 520 + ((dim * 1.35) / 2) + ((dim * 1.35) / 6)
                }}
            >
                {selecteddata.msg}
            </div>
            : null}

        {(selecteddata && selecteddata.name)
            ? <div
                className="Guestbooklookup_name"
                style={{
                    padding: (dim * 1.35) / 2,
                    paddingTop: 5,
                    height: 70
                }}
            >
                {selecteddata.name}
            </div>
            : null}

    </div>

    return <div ref={containerRef} className="Guestbooklookup_root" style={{ backgroundColor: bgcolor }}>

        {device === 'WEB' && lookuptype === 'SCATTER'
            ? <div
                style={{ position: 'absolute', right: 0, bottom: 0, width: 100, height: 100, margin: 25, border: 'none', backgroundColor: 'transparent' }}
                onClick={async () => {
                    const el = containerRef.current;
                    !document.fullscreenElement ? await el.requestFullscreen() : await document.exitFullscreen();
                }}
            >
            </div>
            : null}

        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', backgroundColor: bgcolor, padding: '10px 10px 50px 10px' }}>

            {lookuptype === 'SCATTER' && titledata && titledata.title && titledata.subtitle
                ? <div className="Guestbooklookup_bubbletitleroot">
                    <div className="Guestbooklookup_bubbletitletext">
                        {titledata.title.text}
                    </div>
                    <div className="Guestbooklookup_bubblesubtitletext">
                        {titledata.subtitle.text}
                    </div>
                </div>
                : null}

            {lookuptype === 'SCATTER' && readyToDraw === 1 && !fullscreen
                ? <GuestScatterChart
                    guestdata={guestdata}
                    guestdataaddr={guestdataaddr}
                    maxguestnum={maxguestnum}
                    bgcolor={bgcolor}
                    dim={dim}
                    height={ht - 22}
                    width={'100%'}
                    nodeclick={(id) => {
                        handleManualSelect(id)
                        setShowportal(1)
                    }}
                    canvasRefs={canvasRefs}
                    properties={properties}
                    sharedproperties={sharedproperties}
                    device={device}
                    selecteddata={selecteddata}
                />
                : null}

            {lookuptype === 'SCATTER' && readyToDraw === 1 && fullscreen
                ? <GuestScatterChartFullscreen
                    guestdata={guestdata}
                    guestdataaddr={guestdataaddr}
                    maxguestnum={maxguestnum}
                    bgcolor={bgcolor}
                    dim={dim}
                    height={ht - 22}
                    width={'100%'}
                    nodeclick={(id) => {
                        handleManualSelect(id)
                    }}
                    canvasRefs={canvasRefs}
                    properties={properties}
                    sharedproperties={sharedproperties}
                    device={device}
                    selecteddata={selecteddata}
                />
                : null}

            {lookuptype === 'GRID' && readyToDraw >= 1
                ? {
                    'ARC': <OwnerArcTitle
                        showgrid={false}
                        ownertitletopbool={false}
                        ownersubtitletopbool={false}
                        device={device}
                        default_svg_properties={default_svg_properties}
                        titledata={titledata}
                        updateTitledata={() => { }}
                        updateOwnertitletopbool={() => { }}
                        updateOwnersubtitletopbool={() => { }}
                        updatePanelAppearPosition={() => { }}
                        updatetitledata={() => { }}
                        updateTitletext={() => { }}
                        updatesubtitledata={() => { }}
                        updateSubtitletext={() => { }}
                        backuptitle={() => { }}
                        backupsubtitle={() => { }}
                        canceltitle={() => { }}
                        cancelsubtitle={() => { }}
                        savetitle={() => { }}
                        savesubtitle={() => { }}
                    />,
                    'TEXT': <></>
                }[titledata['type']]
                : null}

            {/* this will draw the path of the flower */}
            {false && lookuptype === 'GRID' && (readyToDraw === 1 || readyToDraw === 2)
                ? <div
                    className={`Drawbookedit_grid Drawbookedit_grid_${lookuptype}_${device}`}
                    style={{
                        gridTemplateColumns: `repeat(auto-fill, minmax(${maxguestnum === 30 ? 100 : dim}px, 1fr))`,
                        width: maxguestnum === 30 && device === 'WEB' ? '50%' : device === 'WEB' ? '75%' : '100%',
                        placeSelf: 'center',
                        marginTop: '1%',
                        display: 'grid',
                        placeContent: 'center',
                    }}
                >
                    {Array.from({ length: Number(maxguestnum) }).map((_, i) => {
                        let newdim = maxguestnum === 30 ? 100 : dim
                        if (guestdataaddr[i]) {
                            return <div
                                key={guestdataaddr[i] + '_' + i}
                                style={{
                                    width: newdim,
                                    height: newdim,
                                    overflow: 'hidden',
                                    display: 'inline-block',
                                    placeContent: 'center',
                                    placeItems: 'center',
                                    placeSelf: 'center'
                                }}
                                onClick={() => {
                                    setSelecteddata({ ...guestdata[guestdataaddr[i]], addr: guestdataaddr[i] })
                                    setShowportal(1)
                                }}
                            >
                                <canvas
                                    ref={el => { if (el) canvasRefs.current[i] = el; }}
                                    width={300}
                                    height={300}
                                    style={{
                                        transform: `scale(${(maxguestnum === 30 ? 100 : dim) / 300})`,
                                        transformOrigin: 'top left',
                                        display: 'block',
                                        backgroundColor: bgcolor
                                    }}
                                />
                            </div>
                        }
                        else {
                            return <div
                                key={guestdataaddr[i] + '_' + i}
                                style={{ width: newdim, height: newdim, overflow: 'hidden', display: 'flex', placeContent: 'center', placeItems: 'center' }}
                            >
                                {guestbooklookupstub}
                            </div>
                        }
                    })}
                </div>
                : null}

            {/* this will not draw the path */}
            {lookuptype === 'GRID' && (readyToDraw === 1 || readyToDraw === 2)
                ? (
                    <div
                        className={`Drawbookedit_grid Drawbookedit_grid_${lookuptype}_${device}`}
                        style={{
                            gridTemplateColumns: `repeat(auto-fill, minmax(${maxguestnum === 30 ? { 'WEB': 100, 'TABLET': 100, 'MOBILE': '33.33%' }[device] : dimroot}px, 1fr))`,
                            width: maxguestnum === 30 && device === 'WEB' ? '50%' : device === 'WEB' ? '75%' : '100%',
                            placeSelf: 'center',
                            marginTop: '1%',
                            placeContent: 'center',

                        }}
                    >
                        {Array.from({ length: Number(maxguestnum) }).map((_, i) => {

                            let newdimroot = 0
                            let newdim = 0

                            if (maxguestnum === 30) {
                                newdimroot = 100
                                newdim = 100
                            }
                            else {
                                newdimroot = dimroot
                                newdim = dim
                            }



                            if (guestdataaddr[i]) {
                                const guest = guestdata[guestdataaddr[i]];
                                return (
                                    <div
                                        key={guestdataaddr[i] + '_' + i}
                                        style={{
                                            // bear
                                            width: newdimroot,
                                            height: newdimroot,
                                            display: 'flex',
                                            overflow: 'hidden',
                                            placeContent: 'center',
                                            placeItems: 'center',
                                            placeSelf: 'center',
                                        }}
                                        onClick={() => {
                                            setSelecteddata({ ...guest, addr: guestdataaddr[i] });
                                            setShowportal(1);
                                        }}
                                    >
                                        {guest?.image
                                            ? <img
                                                src={guest.image}
                                                alt="guest drawing"
                                                crossOrigin="anonymous"
                                                style={{
                                                    width: newdim,
                                                    height: newdim,
                                                    display: 'block',
                                                    objectFit: 'cover',
                                                    backgroundColor: bgcolor
                                                }}
                                            />
                                            : guestdataaddr.length < 25
                                                ? <div
                                                    style={{
                                                        width: newdimroot,
                                                        height: newdimroot,
                                                        display: 'flex',
                                                        placeContent: 'center',
                                                        placeItems: 'center',
                                                        backgroundColor: bgcolor,
                                                        placeContent: 'center',
                                                        placeItems: 'center'
                                                    }}
                                                >
                                                    {guestbooklookupstub}
                                                </div>
                                                : null
                                        }
                                    </div>
                                );
                            }
                            else {
                                if (guestdataaddr.length < 25) {
                                    return (
                                        <div
                                            key={guestdataaddr[i] + '_' + i}
                                            style={{
                                                width: newdimroot,
                                                height: newdimroot,
                                                overflow: 'hidden',
                                                display: 'flex',
                                                placeContent: 'center',
                                                placeItems: 'center'
                                            }}
                                        >
                                            {guestbooklookupstub}
                                        </div>
                                    );
                                }
                                else {
                                    return null
                                }

                            }

                        })}
                    </div>
                )
                : null}

        </div>

        <Guestbookportal
            portaltype={'commonportal'}
            open={showportal}
            floatingclose={false}
            backdropvisible={true}
            bgcolor={bgcolor}
            closestyle={{ right: 0 }}
            floatingclosecolor='#333333'
        >
            {{ 'WEB': WEB_PORTAL_CONTENT, 'TABLET': WEB_PORTAL_CONTENT, 'MOBILE': MOBILE_PORTAL_CONTENT }[device]}
        </Guestbookportal>

    </div>

}

export default Guestbooklookup